import React, { useEffect, useState } from 'react';
import { UserDto } from '@trucktrax/trucktrax-ts-common';
import { getUserByUrl } from '../../services/usersService';
import { driverFullName } from '../../util/driverUtil';

export interface ResolvedUserNameProps {
  /** User URL to resolve; the fallback renders while absent or unresolved. */
  url?: string;
  className?: string;
  dataTest?: string;
  /** Rendered when the url is absent, still resolving, or the fetch fails. */
  fallback?: JSX.Element | null;
}

/**
 * DEV-4153: renders a user's display name from a user URL. Owns the fetch and
 * its stale guard — a slow response for a previous url must not overwrite the
 * name once the url prop has moved on (the url can change without a remount,
 * e.g. flipping between tickets in the drill-down). Falls back to the
 * username for users missing name parts.
 */
export function ResolvedUserName({
  url,
  className,
  dataTest,
  fallback = null,
}: ResolvedUserNameProps) {
  const [name, setName] = useState<string | undefined>(undefined);

  useEffect(() => {
    let stale = false;
    setName(undefined);
    if (!url) {
      return undefined;
    }

    getUserByUrl(url)
      .then((user: UserDto) => {
        if (!stale) {
          setName(driverFullName(user) || user?.username);
        }
      })
      .catch(() => {
        // Leave unresolved — the fallback renders; the surrounding context
        // (e.g. the acknowledgement timestamp) still shows.
      });

    return () => {
      stale = true;
    };
  }, [url]);

  if (!name) {
    return fallback;
  }

  return (
    <div className={className} data-test={dataTest}>
      {name}
    </div>
  );
}

export default ResolvedUserName;
